import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Animates elements from where they used to be to where they are now.
 *
 * Records each registered element's position after every render, and on the
 * next one animates it from the old position to the new. The element itself
 * may be a different node than last time, which is what makes this work for a
 * list where React remounts an item under a new parent: the animation runs on
 * whatever node now carries that key.
 *
 * Motion is the point here rather than decoration. A reviewer matching waste
 * lines is saying "this vendor line is really that one", and seeing it travel
 * to the line it was matched with is the confirmation that the click did what
 * they meant. Skipped entirely under prefers-reduced-motion, where the list
 * simply redraws in its new order.
 */
/**
 * Where the element sits within the content of the box that scrolls it, so
 * scrolling between two renders does not read as the element having moved.
 * A drag scrolls the dialog while re-rendering on every dragover.
 */
function contentTop(node: HTMLElement): number {
  let scrolled = 0;
  for (let el = node.parentElement; el != null; el = el.parentElement) {
    scrolled += el.scrollTop;
  }
  return node.getBoundingClientRect().top + scrolled;
}

export function useReorderAnimation(durationMs = 220) {
  const nodes = useRef(new Map<string, HTMLElement>());
  const positions = useRef(new Map<string, number>());
  const callbacks = useRef(new Map<string, (node: HTMLElement | null) => void>());

  useLayoutEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    for (const [key, node] of nodes.current) {
      const before = positions.current.get(key);
      const after = contentTop(node);
      positions.current.set(key, after);

      // Nothing to animate the first time a key is seen, when it has not moved,
      // or when the reader asked for no motion.
      if (reduced || before == null) continue;
      const travelled = before - after;
      if (Math.abs(travelled) < 2) continue;
      if (typeof node.animate !== "function") continue;

      node.animate([{ transform: `translateY(${travelled}px)` }, { transform: "translateY(0)" }], {
        duration: durationMs,
        easing: "cubic-bezier(0.2, 0, 0, 1)",
      });
    }

    // Forget a key nothing rendered this time, so a line that leaves and comes
    // back animates in rather than from where it used to sit. Done here rather
    // than when the element detaches: an item moving between parents detaches
    // and reattaches in the same commit, and its old position is exactly what
    // the next pass needs.
    const gone: string[] = [];
    for (const key of positions.current.keys()) {
      if (!nodes.current.has(key)) gone.push(key);
    }
    for (const key of gone) {
      positions.current.delete(key);
      callbacks.current.delete(key);
    }
  });

  /**
   * Ref callback for one item, the same function every render.
   *
   * Identity matters: React reacts to a new ref callback by calling the old one
   * with null and the new one with the node, so a callback built inline during
   * render would detach and reattach every time, and the position recorded for
   * the key would never survive to be animated from.
   */
  const register = useCallback((key: string) => {
    const existing = callbacks.current.get(key);
    if (existing) return existing;

    const callback = (node: HTMLElement | null) => {
      if (node) nodes.current.set(key, node);
      else nodes.current.delete(key);
    };
    callbacks.current.set(key, callback);
    return callback;
  }, []);

  return register;
}
